import { useAckRecoveryCodes } from "@via/pages/authentication/mfa/mfa.api.tsx";
import { type JSX, useState } from "react";

const SECONDARY_BUTTON =
  "rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50";

/** Shows freshly issued recovery codes once, and records that the user saved them. */
export default function RecoveryCodesPanel({
  codes,
  onDone,
  doneLabel = "Continue",
}: {
  codes: string[];
  onDone: () => void;
  doneLabel?: string;
}): JSX.Element {
  const ack = useAckRecoveryCodes();
  const [saved, setSaved] = useState(false);
  const text = codes.join("\n");

  const download = () => {
    const url = URL.createObjectURL(
      new Blob([`Foundry Connect recovery codes\n\n${text}\n`], {
        type: "text/plain",
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "foundry-connect-recovery-codes.txt";
    link.click();
    // Revoking in the same tick can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="space-y-4">
      <h2 className="via-h2 text-2xl font-bold">Save your recovery codes</h2>
      <p className="text-sm text-gray-600">
        If you lose your phone, each code signs you in once. They are shown only
        now, so keep them somewhere safe, away from your phone.
      </p>
      <ul
        data-testid="mfa-recovery-codes"
        className="grid grid-cols-2 gap-2 rounded-md bg-gray-50 p-4 font-mono text-sm">
        {codes.map(code => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => void navigator.clipboard?.writeText(text)}>
          Copy
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={download}>
          Download
        </button>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-900">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          checked={saved}
          onChange={e => setSaved(e.target.checked)}
        />
        I saved these codes somewhere safe
      </label>
      {ack.isError && (
        <p role="alert" className="text-sm font-semibold text-rose-600">
          Something went wrong. Please try again.
        </p>
      )}
      <button
        type="button"
        disabled={!saved || ack.isPending}
        onClick={() => ack.mutate(undefined, { onSuccess: onDone })}
        className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50">
        {doneLabel}
      </button>
    </div>
  );
}
